import type { BusinessVertical } from "./vertical";

// ---------------------------------------------------------------------------
// Vertical glossary (GRA-83)
//
// The single source of DVH terminology. The web app's DVH overlay test, the
// coach system prompt and the insight prompts all read from here, so a term
// changes in one place. Draft for Viktor/Jonas to review (GRA-82 glossary
// question); every entry is a Swedish term pair.
// ---------------------------------------------------------------------------

export interface GlossaryTerm {
  /** Singular, indefinite ("säljare"). */
  one: string;
  /** Plural, indefinite ("säljare"). */
  many: string;
  /** Singular, definite ("säljaren"). */
  oneDefinite: string;
  /** Plural, definite ("säljarna"). */
  manyDefinite: string;
}

export interface VerticalGlossary {
  /** The person whose results are coached: säljare / medarbetare. */
  employee: GlossaryTerm;
  /** The unit a leader leads: team / avdelning. */
  team: GlossaryTerm;
  /** The person who leads it: säljchef / avdelningschef. */
  leader: GlossaryTerm;
  /** The whole organisation: säljorganisation / butik. */
  organisation: GlossaryTerm;
  /** The goals-and-routines document: spelbok / butiksdrift. */
  playbook: GlossaryTerm;
  /** The thing being won: affär / – (no DVH equivalent; "försäljning"). */
  deal: GlossaryTerm;
  /** Short description of the business for prompts. */
  businessDescription: string;
}

const term = (
  one: string,
  many: string,
  oneDefinite: string,
  manyDefinite: string,
): GlossaryTerm => ({ one, many, oneDefinite, manyDefinite });

export const VERTICAL_GLOSSARY: Record<BusinessVertical, VerticalGlossary> = {
  sales: {
    employee: term("säljare", "säljare", "säljaren", "säljarna"),
    team: term("team", "team", "teamet", "teamen"),
    leader: term("säljchef", "säljchefer", "säljchefen", "säljcheferna"),
    organisation: term(
      "säljorganisation",
      "säljorganisationer",
      "säljorganisationen",
      "säljorganisationerna",
    ),
    playbook: term("spelbok", "spelböcker", "spelboken", "spelböckerna"),
    deal: term("affär", "affärer", "affären", "affärerna"),
    businessDescription: "en svensk B2B-säljorganisation",
  },
  dvh: {
    employee: term("medarbetare", "medarbetare", "medarbetaren", "medarbetarna"),
    team: term("avdelning", "avdelningar", "avdelningen", "avdelningarna"),
    leader: term(
      "avdelningschef",
      "avdelningschefer",
      "avdelningschefen",
      "avdelningscheferna",
    ),
    organisation: term("butik", "butiker", "butiken", "butikerna"),
    playbook: term("butiksdrift", "butiksdrift", "butiksdriften", "butiksdriften"),
    deal: term("försäljning", "försäljning", "försäljningen", "försäljningen"),
    businessDescription:
      "en svensk livsmedelsbutik (dagligvaruhandel, t.ex. ICA eller Coop) med avdelningar",
  },
};

export function glossaryFor(vertical: BusinessVertical): VerticalGlossary {
  return VERTICAL_GLOSSARY[vertical];
}

/**
 * Sales words that must not appear in DVH copy. The web app overlay test
 * fails when a Swedish string contains one of these without a DVH override.
 */
export const DVH_FORBIDDEN_SALES_TERMS = [
  "säljare",
  "säljaren",
  "säljarna",
  "säljchef",
  "säljteam",
  "spelbok",
  "spelboken",
  "pipeline",
  "affär",
  "affärer",
] as const;

// ---------------------------------------------------------------------------
// Prompt localisation
//
// The coach and insight prompts are written for sales. For DVH they are run
// through these replacements instead of being duplicated. Matching is on
// whole words: a letter, digit, `_`, `-`, `` ` `` or `{` on either side
// blocks a match, so tool names (`intelligence_get_team_pulse`), ids and
// template placeholders are never touched. Order matters: inflected forms
// come before their stems.
// ---------------------------------------------------------------------------

const DVH_REPLACEMENTS: [string, string][] = [
  // Phrases first: "team" is neuter and has the same plural, "avdelning" is
  // common gender, so articles and plurals need whole phrases.
  ["Team Collab", "Samarbete"],
  ["Teamet är skapat", "Avdelningen är skapad"],
  ["teamet är skapat", "avdelningen är skapad"],
  ["Ditt team", "Din avdelning"],
  ["ditt team", "din avdelning"],
  ["Mitt team", "Min avdelning"],
  ["mitt team", "min avdelning"],
  ["Ert team", "Er avdelning"],
  ["ert team", "er avdelning"],
  ["Sitt team", "Sin avdelning"],
  ["sitt team", "sin avdelning"],
  ["Nytt team", "Ny avdelning"],
  ["nytt team", "ny avdelning"],
  ["Detta team", "Denna avdelning"],
  ["detta team", "denna avdelning"],
  ["Vilket team", "Vilken avdelning"],
  ["vilket team", "vilken avdelning"],
  ["hela teamet", "hela avdelningen"],
  ["ett annat team", "en annan avdelning"],
  ["Det här teamet", "Den här avdelningen"],
  ["det här teamet", "den här avdelningen"],
  ["Det teamet", "Den avdelningen"],
  ["det teamet", "den avdelningen"],
  ["ett helt team", "en hel avdelning"],
  ["Ett team", "En avdelning"],
  ["ett team", "en avdelning"],
  ["varje team", "varje avdelning"],
  ["teamnamn", "avdelningsnamn"],
  ["teamöversikt", "avdelningsöversikt"],
  ["teamfokus", "avdelningsfokus"],
  ["Antal team", "Antal avdelningar"],
  ["Inga team", "Inga avdelningar"],
  ["alla team", "alla avdelningar"],
  ["flera team", "flera avdelningar"],
  ["Teammedlemmar", "Medarbetare"],
  ["teammedlemmar", "medarbetare"],
  ["teammedlem", "medarbetare"],
  ["säljardata", "medarbetardata"],
  ["SÄLJARE", "MEDARBETARE"],
  ["SPELBOK", "BUTIKSDRIFT"],
  ["Säljarens", "Medarbetarens"],
  ["säljarens", "medarbetarens"],
  ["Säljarna", "Medarbetarna"],
  ["säljarna", "medarbetarna"],
  ["Säljaren", "Medarbetaren"],
  ["säljaren", "medarbetaren"],
  ["säljares", "medarbetares"],
  ["Säljare", "Medarbetare"],
  ["säljare", "medarbetare"],
  ["säljar-scope", "medarbetar-scope"],
  ["säljarscope", "medarbetarscope"],
  ["säljcheferna", "avdelningscheferna"],
  ["säljchefer", "avdelningschefer"],
  ["säljchefen", "avdelningschefen"],
  ["Säljchef", "Avdelningschef"],
  ["säljchef", "avdelningschef"],
  ["säljteamet", "avdelningen"],
  ["säljteam", "avdelning"],
  ["säljsiffror", "försäljningssiffror"],
  ["säljmanualer", "rutiner"],
  ["säljsamtal", "kundmöten"],
  ["säljcykel", "säsongsmönster"],
  ["dealcykler", "säsongsmönster"],
  ["säljorganisationen", "butiken"],
  ["säljorganisation", "butik"],
  ["B2B-säljorganisation", "livsmedelsbutik"],
  ["Spelboken", "Butiksdriften"],
  ["spelboken", "butiksdriften"],
  ["Spelbok", "Butiksdrift"],
  ["spelbok", "butiksdrift"],
  ["spelboksmål", "mål för butiksdriften"],
  ["affärsmål", "försäljningsmål"],
  ["Affärer", "Försäljning"],
  ["affärerna", "försäljningen"],
  ["affärer", "försäljning"],
  ["affären", "försäljningen"],
  ["affär", "försäljning"],
  ["snittaffär", "snittköp"],
  ["Snittaffär", "Snittköp"],
  ["pipeline", "försäljning"],
  ["Teamets", "Avdelningens"],
  ["teamets", "avdelningens"],
  ["Teamen", "Avdelningarna"],
  ["teamen", "avdelningarna"],
  ["Teamet", "Avdelningen"],
  ["teamet", "avdelningen"],
  ["Team", "Avdelning"],
  ["team", "avdelning"],
];

const WORD_EDGE = "[\\p{L}\\p{N}_`{}\\-]";

const DVH_REPLACEMENT_PATTERNS: [RegExp, string][] = DVH_REPLACEMENTS.map(
  ([from, to]) => [
    new RegExp(`(?<!${WORD_EDGE})${from}(?!${WORD_EDGE})`, "gu"),
    to,
  ],
);

/**
 * Phrases that look like sales terms but are not about the customer's
 * organisation (Graftus' own staff), kept verbatim.
 */
const DVH_PROTECTED_PHRASES = ["Vårt team", "vårt team"];

/**
 * Rewrites sales wording into the vertical's terms. A no-op for "sales".
 * Use on platform-authored prompt text only, never on user data (names,
 * team names, notes), which must reach the model unchanged.
 */
export function localizeForVertical(
  text: string,
  vertical: BusinessVertical,
): string {
  if (vertical === "sales") return text;
  // Park protected phrases behind non-letter sentinels while replacing.
  let out = text;
  DVH_PROTECTED_PHRASES.forEach((phrase, i) => {
    out = out.split(phrase).join(`\u0000${i}\u0000`);
  });
  for (const [pattern, to] of DVH_REPLACEMENT_PATTERNS) {
    out = out.replace(pattern, to);
  }
  DVH_PROTECTED_PHRASES.forEach((phrase, i) => {
    out = out.split(`\u0000${i}\u0000`).join(phrase);
  });
  return out;
}

/** Forbidden sales words found in `text` (whole words, case-insensitive). */
export function findSalesTerms(text: string): string[] {
  const found = new Set<string>();
  for (const word of DVH_FORBIDDEN_SALES_TERMS) {
    const re = new RegExp(`(?<!${WORD_EDGE})${word}(?!${WORD_EDGE})`, "giu");
    if (re.test(text)) found.add(word);
  }
  return [...found];
}
